import { collection, doc } from 'firebase/firestore'
import { useQueryClient } from 'react-query'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useInfiniteQuery } from 'hooks/useInfiniteQuery'
import { useMutation } from 'hooks/useMutation'

import { db } from 'utils/firebase'
import { createMessage as createMessageQuery, fetchMessages } from 'utils/queries'
import { Toast } from 'utils/toast'
import { createPartialUser } from 'utils/user/createPartialUser'

import { Message } from 'types/chat'

interface Props {
  roomId: string
}

export const useMessages = ({ roomId }: Props) => {
  const { data: user } = useCurrentUser()
  const queryClient = useQueryClient()
  const sortOrder = 'created'

  const messagesQuery = useInfiniteQuery<Array<Message>>(
    ['messages', roomId],
    ({ pageParam }) =>
      fetchMessages(
        { roomId, currentUserId: user?.id },
        { order: sortOrder, limit: 20, lastVisible: pageParam },
      ),
    {
      getNextPageParam: (lastPage) => {
        // Very important, as the last page might not have any results
        if (!lastPage || !lastPage.length) return undefined

        return lastPage[lastPage.length - 1][sortOrder]
      },
      staleTime: Infinity,
    },
  )

  const createMessage = useMutation<unknown, { messageBody: string }>(
    ({ messageBody }) => {
      const messageId = doc(collection(db, 'rooms', roomId, 'messages')).id
      if (!user) {
        throw new Error('User not found...')
      }

      return {
        messageId,
        ...createMessageQuery({ messageId, messageBody, roomId, user }),
      }
    },
    {
      onSuccess: (data, variables) => {
        if (!user) {
          throw new Error('User not found...')
        }

        const message: Message = {
          id: data?.messageId,
          body: variables.messageBody,
          created: Date.now(),
          creator: createPartialUser(user),
        }

        const messages = queryClient.getQueryData(['messages', roomId])
        messages.pages[0].unshift(message)

        queryClient.invalidateQueries(['messages', roomId])
      },
      onError: () => {
        Toast.error('Unable to create message', 'Please try again')
      },
    },
  )

  return {
    ...messagesQuery,

    // Mutations
    createMessage,
  }
}
